// client/src/pages/TaskDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_API_URL}/api`;

export default function TaskDetails() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    if (!taskId) return;

    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/tasks/${taskId}`);
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || "Failed to load task");
        setTask(d.task || d);
      } catch (err) {
        alert(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [taskId]);

  const isOwner =
    task && user && String(task.postedBy) === String(user.id);

  const isAccepted =
    task &&
    task.acceptedApplicant &&
    String(task.acceptedApplicant) === String(user?.id);

  const alreadyApplied =
    task?.applicants?.some((a) => String(a.userId) === String(user?.id));

  /* ============================================================
      CREATE CHAT SESSION → THEN NAVIGATE 
      (Fixes: Chat With Owner not working & fresh chat after removal)
  ============================================================ */
  const openChat = async (participantId) => {
    try {
      const token = localStorage.getItem("cp_token");

      // FORCE create new chat session
      await fetch(`${API}/chat/start/task/${taskId}/${participantId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      navigate(`/chat/task/${taskId}/${participantId}`);
    } catch (err) {
      console.error("Chat start error:", err);
      navigate(`/chat/task/${taskId}/${participantId}`);
    }
  };

  /* ACTION HANDLER */
  const sendAction = async (endpoint, body = {}) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem("cp_token");

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Action failed");

      const reload = await fetch(`${API}/tasks/${taskId}`);
      const updated = await reload.json();

      setTask(updated.task || updated);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApply = async () => {
    if (!user) return navigate("/login");
    if (task.acceptedApplicant)
      return alert("Task already assigned.");

    await sendAction(`${API}/tasks/${taskId}/apply`);
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel your application?")) return;
    await sendAction(`${API}/tasks/${taskId}/cancel`);
  };

  const markCompleted = async () => {
    if (!isAccepted)
      return alert("Only accepted applicant can mark completion.");

    try {
      setMarking(true);
      const token = localStorage.getItem("cp_token");

      const res = await fetch(`${API}/tasks/${taskId}/mark-completed`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      const reload = await fetch(`${API}/tasks/${taskId}`);
      const updated = await reload.json();
      setTask(updated.task || updated);

      alert("Marked completed — waiting for owner confirmation.");
    } catch (err) {
      alert(err.message);
    } finally {
      setMarking(false);
    }
  };

  const confirmCompletion = async () => {
    if (!isOwner) return;

    if (!window.confirm(`Confirm completion and give ${rating} star(s)?`))
      return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem("cp_token");

      const res = await fetch(
        `${API}/tasks/${taskId}/confirm-completion`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ rating }),
        }
      );

      const d = await res.json();
      if (!res.ok) throw new Error(d.error);

      const reload = await fetch(`${API}/tasks/${taskId}`);
      const updated = await reload.json();

      setTask(updated.task || updated);
      setConfirmOpen(false);

      alert("Task completed and applicant rated.");
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading)
    return (
      <div className="p-6 text-white min-h-screen">
        Loading task...
      </div>
    );

  if (!task)
    return (
      <div className="p-6 text-center text-red-600">
        Task not found
      </div>
    );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
      <div className="max-w-5xl mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8">

        {/* HEADER */}
        <div className="flex justify-between">
          <div>
            <h1 className="text-3xl font-bold dark:text-slate-200">{task.title}</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {task.category} • {new Date(task.createdAt).toLocaleString()}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
            Posted by{" "}
            <span className="font-medium">
              {task.postedByName || task.postedBy || "Anonymous"}
            </span>
          </p>
          </div>

          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded-lg hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Back
          </button>
        </div>

        {/* IMAGE */}
        {task.imageUrl && (
          <img
            src={task.imageUrl}
            className="w-full h-64 object-cover rounded-xl mt-6"
            alt=""
          />
        )}

        {/* DESCRIPTION */}
        <p className="mt-6 whitespace-pre-line text-slate-700 dark:text-slate-200">
          {task.description}
        </p>

        {/* STATS GRID */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 dark:text-slate-200">
          <StatCard label="Budget" value={`₹${task.budgetMin} - ₹${task.budgetMax}`} />
          <StatCard label="Deadline" value={task.dueDate ? new Date(task.dueDate).toLocaleString() : "N/A"} />
          <StatCard label="Applicants" value={task.applicants?.length || 0} />
          <StatCard label="Status" value={task.status} highlight />
        </div>

        {/* ACTION BUTTONS */}
        <div className="mt-8 flex flex-wrap gap-3">

          {/* APPLY BUTTON */}
          {!isOwner && !isAccepted && !task.acceptedApplicant && (
            <button
              disabled={actionLoading}
              onClick={handleApply}
              className={`px-5 py-3 text-white rounded-lg ${
                alreadyApplied ? "bg-yellow-600" : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              {alreadyApplied ? "Applied" : "Apply"}
            </button>
          )}

          {/* APPLIED BUT NOT ACCEPTED MESSAGE */}
          {!isOwner && alreadyApplied && !isAccepted && task.acceptedApplicant && (
            <div className="px-5 py-3 bg-yellow-200 text-yellow-800 rounded-lg">
              Somebody else is selected — You may wait or cancel (no penalty).
            </div>
          )}

          {/* CANCEL APPLICATION */}
          {!isOwner && alreadyApplied && !isAccepted && (
            <button
              onClick={handleCancel}
              className="px-5 py-3 bg-red-600 rounded-lg text-white"
            >
              Cancel Application
            </button>
          )}

          {/* ACCEPTED USER CHAT */}
          {isAccepted && (
            <button
              onClick={() => openChat(user.id)}
              className="px-5 py-3 bg-green-600 rounded-lg text-white hover:bg-green-700"
            >
              Chat With Owner
            </button>
          )}

          {/* MARK COMPLETED */}
          {isAccepted && task.status === "in_progress" && (
            <button
              onClick={markCompleted}
              className="px-5 py-3 bg-orange-600 rounded-lg text-white"
            >
              {marking ? "Marking…" : "Mark Completed"}
            </button>
          )}

          {/* OWNER REVIEW */}
          {isOwner && task.status === "pending_completion" && (
            <button
              onClick={() => setConfirmOpen(true)}
              className="px-5 py-3 bg-indigo-600 rounded-lg text-white"
            >
              Review Completion
            </button>
          )}

          {/* OWNER CHAT WITH ASSIGNEE */}
          {isOwner && task.acceptedApplicant && (
            <button
              onClick={() => openChat(task.acceptedApplicant)}
              className="px-5 py-3 bg-green-600 rounded-lg text-white"
            >
              Chat With Assignee
            </button>
          )}
        </div>

        {/* APPLICANTS LIST */}
        {isOwner && task.applicants?.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold mb-3">Applicants</h2>

            {task.applicants.map((a) => (
              <ApplicantCard
                key={a.userId}
                applicant={a}
                task={task}
                taskId={taskId}
                sendAction={sendAction}
              />
            ))}
          </div>
        )}
      </div>

      {/* MODAL */}
      {confirmOpen && (
        <ConfirmModal
          rating={rating}
          setRating={setRating}
          actionLoading={actionLoading}
          onClose={() => setConfirmOpen(false)}
          onConfirm={confirmCompletion}
        />
      )}
    </div>
  );
}

/* ---------------------------------------
   COMPONENTS
----------------------------------------*/

function StatCard({ label, value, highlight }) {
  return (
    <div className="p-4 rounded-xl bg-slate-100 dark:bg-slate-900">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`font-semibold ${highlight ? "text-indigo-500" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function ApplicantCard({ applicant, task, taskId, sendAction }) {
  const isAccepted = String(task.acceptedApplicant) === String(applicant.userId);

  return (
    <div className="p-4 rounded-xl bg-slate-100 dark:bg-slate-900 shadow flex justify-between items-center mb-3">

      {/* LEFT SIDE */}
      <div>
        <p className="font-medium dark:text-white">{applicant.name}</p>
        <p className="text-xs text-slate-500">{applicant.email}</p>

        {/* CREDIT SCORE BADGE */}
        <div className="relative group mt-1">
          <span className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-full">
            Score: {applicant.creditScore ?? "??"}
          </span>

          {/* TOOLTIP */}
          <div className="absolute top-full left-0 mt-1 hidden group-hover:block bg-black text-white text-xs p-2 rounded shadow-lg w-48">
            Higher score = higher trust.  
            Completing tasks increases your score!
          </div>
        </div>
      </div>

      {/* RIGHT SIDE BUTTONS */}
      <div className="flex gap-2">

        {isAccepted ? (
          <>
            <span className="text-green-600 font-bold">Accepted</span>

            <button
              onClick={() =>
                sendAction(`${API}/tasks/${taskId}/reject`, {
                  applicantId: applicant.userId,
                })
              }
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Remove
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() =>
                sendAction(`${API}/tasks/${taskId}/accept`, {
                  applicantId: applicant.userId,
                })
              }
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Accept
            </button>

            <button
              onClick={() =>
                sendAction(`${API}/tasks/${taskId}/reject`, {
                  applicantId: applicant.userId,
                })
              }
              className="px-4 py-2 border rounded-lg dark:hover:bg-slate-700 hover:bg-slate-200"
            >
              Remove
            </button>
          </>
        )}

      </div>
    </div>
  );
}

function ConfirmModal({ rating, setRating, onClose, onConfirm, actionLoading }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-xl w-full max-w-md">
        <h3 className="text-xl font-bold">Confirm Completion & Rate</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
          Rate the applicant (1–5 stars)
        </p>

        <div className="mt-4 flex gap-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              onClick={() => setRating(s)}
              className={`text-2xl ${
                s <= rating ? "text-yellow-400" : "text-gray-300"
              }`}
            >
              ★
            </button>
          ))}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded border"
          >
            Cancel
          </button>

          <button
            onClick={onConfirm}
            disabled={actionLoading}
            className="px-4 py-2 bg-indigo-600 text-white rounded"
          >
            {actionLoading ? "Saving…" : "Confirm & Rate"}
          </button>
        </div>
      </div>
    </div>
  );
}
